import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { AlertTriangle, CheckCircle, Clock, Edit2, Eye, FileEdit, FileText, FileType2, Image as ImageIcon, Link2, Megaphone, MessageSquare, Phone, Plus, RefreshCw, RotateCw, Search, Send, ShieldCheck, Sparkles, Trash2, Video, XCircle } from "lucide-react";
import { WhatsAppPreview, type Button_, type TemplateForm } from "@/components/templates/WhatsAppPreview";

// ─── Helpers visuais ─────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { icon: any; label: string; color: string; bg: string; border: string }> = {
  draft:    { icon: FileEdit,    label: "Rascunho",  color: "text-slate-700",  bg: "bg-slate-100",    border: "border-slate-200" },
  pending:  { icon: Clock,       label: "Pendente",  color: "text-amber-700",  bg: "bg-amber-50",     border: "border-amber-200" },
  approved: { icon: CheckCircle, label: "Aprovado",  color: "text-emerald-700",bg: "bg-emerald-50",   border: "border-emerald-200" },
  rejected: { icon: XCircle,     label: "Rejeitado", color: "text-rose-700",   bg: "bg-rose-50",      border: "border-rose-200" },
};

const CATEGORY_CONFIG: Record<string, { icon: any; label: string; color: string; bg: string }> = {
  MARKETING:      { icon: Megaphone,   label: "Marketing",     color: "text-purple-700", bg: "bg-purple-50" },
  UTILITY:        { icon: Sparkles,    label: "Utilidade",     color: "text-blue-700",   bg: "bg-blue-50" },
  AUTHENTICATION: { icon: ShieldCheck, label: "Autenticação",  color: "text-cyan-700",   bg: "bg-cyan-50" },
};

const HEADER_ICONS: Record<string, any> = {
  IMAGE: ImageIcon, VIDEO: Video, DOCUMENT: FileType2, TEXT: FileText,
};

function CategoryBadge({ category }: { category: string }) {
  const cfg = CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG.MARKETING;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md font-medium ${cfg.color} ${cfg.bg}`}>
      <cfg.icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

/**
 * Quanto tempo o aviso de recategorização fica no cartão.
 *
 * A Meta reclassifica modelo por conta própria e avisa só por e-mail, para o
 * dono da conta — quem opera o LeadConversa não ficava sabendo. E a diferença
 * pesa: modelo MARKETING custa mais e entra no limite de marketing por
 * usuário. Trinta dias é tempo de alguém abrir a tela e ver.
 */
const DIAS_DE_AVISO_DE_CATEGORIA = 30;

function AvisoDeRecategorizacao({
  anterior,
  atual,
  quando,
}: {
  anterior?: string | null;
  atual: string;
  quando?: string | Date | null;
}) {
  if (!anterior || !quando) return null;
  const data = new Date(quando);
  if (Number.isNaN(data.getTime())) return null;
  const dias = (Date.now() - data.getTime()) / 86_400_000;
  if (dias > DIAS_DE_AVISO_DE_CATEGORIA) return null;

  const rotulo = (c: string) => CATEGORY_CONFIG[c]?.label ?? c;
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50/70 px-2 py-1.5">
      <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5 text-amber-600" strokeWidth={2} />
      <p className="text-[11px] leading-snug text-amber-900">
        A Meta mudou a categoria de <strong>{rotulo(anterior)}</strong> para{' '}
        <strong>{rotulo(atual)}</strong> em {data.toLocaleDateString('pt-BR')}.
        {atual === 'MARKETING' && ' Disparos deste modelo passam a contar como marketing.'}
      </p>
    </div>
  );
}

// ─── Card de template ────────────────────────────────────────────────────────

type TemplateCardProps = {
  t: any;
  onEdit: (t: any) => void;
  onDelete: (id: number) => void;
  onPreview: (t: any) => void;
  onSubmit: (t: any) => void;
  onCheckStatus: (t: any) => void;
  submitPending: boolean;
  checkPending: boolean;
};

function TemplateCard({ t, onEdit, onDelete, onPreview, onSubmit, onCheckStatus, submitPending, checkPending }: TemplateCardProps) {
  const headerImage = t.headerType === "IMAGE" && t.headerContent ? t.headerContent : null;
  const HeaderIcon = t.headerType && t.headerType !== "none" && t.headerType !== "TEXT" ? HEADER_ICONS[t.headerType] : null;
  const buttons: Button_[] = Array.isArray(t.buttons) ? t.buttons : [];
  const cfg = STATUS_CONFIG[t.status] ?? STATUS_CONFIG.draft;

  return (
    <Card className="group hover:shadow-md hover:border-emerald-200 transition-all overflow-hidden">
      <CardContent className="p-3.5 space-y-2">
        {/* Linha 1: nome + status indicator */}
        <div className="flex items-start gap-2">
          {headerImage && (
            <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-muted">
              <img src={headerImage} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          {!headerImage && HeaderIcon && (
            <div className="w-10 h-10 rounded-md shrink-0 bg-emerald-50 flex items-center justify-center">
              <HeaderIcon className="h-4 w-4 text-emerald-600" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm leading-tight truncate" title={t.name}>{t.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CategoryBadge category={t.category} />
              <span className="text-[10px] text-muted-foreground">{t.language}</span>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${cfg.color} ${cfg.bg}`}
            title={cfg.label}
          >
            <cfg.icon className="h-2.5 w-2.5" />
            {cfg.label}
          </span>
        </div>

        <AvisoDeRecategorizacao
          anterior={t.previousCategory}
          atual={t.category}
          quando={t.categoryChangedAt}
        />

        {t.headerType === "TEXT" && t.headerContent && (
          <p className="text-[13px] font-semibold text-foreground line-clamp-1">{t.headerContent}</p>
        )}

        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{t.bodyText}</p>

        {buttons.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {buttons.slice(0, 3).map((b, i) => {
              const Icon = b.type === "URL" ? Link2 : b.type === "PHONE_NUMBER" ? Phone : MessageSquare;
              return (
                <span key={i} className="text-[10px] inline-flex items-center gap-1 bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded-md">
                  <Icon className="h-2.5 w-2.5" />
                  {b.text}
                </span>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-1 pt-2 border-t border-border/60">
          <div className="flex items-center gap-1">
            {t.status === "draft" && (
              <Button
                variant="default" size="sm" className="h-6 text-[11px] px-2"
                disabled={submitPending}
                onClick={() => { if (confirm("Enviar template para aprovação na Meta?")) onSubmit(t); }}
              >
                <Send className="h-3 w-3 mr-1" />
                {submitPending ? "Enviando..." : "Enviar"}
              </Button>
            )}
            {t.status === "pending" && (
              <Button
                variant="outline" size="sm" className="h-6 text-[11px] px-2"
                disabled={checkPending}
                onClick={() => onCheckStatus(t)}
              >
                <RotateCw className={`h-3 w-3 mr-1 ${checkPending ? "animate-spin" : ""}`} />
                Verificar
              </Button>
            )}
          </div>
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onPreview(t)} title="Preview">
              <Eye className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(t)} title="Editar">
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => { if (confirm("Remover template?")) onDelete(t.id); }}
              title="Remover"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Stats bar ───────────────────────────────────────────────────────────────

function StatsBar({ templates }: { templates: any[] | undefined }) {
  const stats = useMemo(() => {
    const list = templates ?? [];
    return {
      total: list.length,
      approved: list.filter((t) => t.status === "approved").length,
      pending: list.filter((t) => t.status === "pending").length,
      draft: list.filter((t) => t.status === "draft").length,
      rejected: list.filter((t) => t.status === "rejected").length,
    };
  }, [templates]);

  const items = [
    { label: "Aprovados", value: stats.approved, icon: CheckCircle, color: "text-emerald-600" },
    { label: "Pendentes", value: stats.pending, icon: Clock, color: "text-amber-600" },
    { label: "Rascunhos", value: stats.draft, icon: FileEdit, color: "text-slate-600" },
    { label: "Rejeitados", value: stats.rejected, icon: XCircle, color: "text-rose-600" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {items.map((item) => (
        <div key={item.label} className="rounded-lg border bg-card px-3 py-2 flex items-center gap-2.5">
          <item.icon className={`h-4 w-4 ${item.color}`} strokeWidth={2} />
          <div className="flex items-baseline gap-1.5">
            <p className="text-lg font-semibold leading-none">{item.value}</p>
            <p className="text-xs text-muted-foreground">{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Página principal ───────────────────────────────────────────────────────

const emptyForm: TemplateForm = {
  name: "", category: "MARKETING", language: "pt_BR",
  headerType: "none", headerContent: "", bodyText: "",
  footerText: "", buttons: [], variableCount: 0, notes: "",
};

export default function Templates() {
  const [, navigate] = useLocation();
  const [previewOnly, setPreviewOnly] = useState<TemplateForm | null>(null);
  const [previewName, setPreviewName] = useState<string>("");
  const [previewId, setPreviewId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const utils = trpc.useUtils();

  const { data: templates, isLoading } = trpc.templates.list.useQuery();

  // Os templates na tela, com o status de aprovação — é o que decide se dá
  // para usar numa transmissão.
  useContextoDeLista({
    titulo: "Templates do WhatsApp",
    itens: (templates ?? []).map((t: any) => ({ id: t.id, nome: t.name, status: t.status, categoria: t.category, idioma: t.language })),
  });


  const deleteMut = trpc.templates.delete.useMutation({
    onSuccess: () => { utils.templates.list.invalidate(); toast.success("Template removido!"); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const submitMut = trpc.templateSync.submitForApproval.useMutation({
    onSuccess: (data) => { utils.templates.list.invalidate(); data.success ? toast.success(data.message) : toast.error(data.message); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const checkStatusMut = trpc.templateSync.updateStatus.useMutation({
    onSuccess: (data) => { utils.templates.list.invalidate(); data.success ? toast.success(data.message) : toast.error(data.message); },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });
  const syncMut = trpc.whatsapp.syncTemplates.useMutation({
    onSuccess: (data) => {
      utils.templates.list.invalidate();
      // Um modelo com erro não derruba o sync, então "deu certo" tem de dizer
      // quantos ficaram para trás — senão parece que tudo foi atualizado.
      const resumo = `${data.imported} novo(s) e ${data.updated} atualizado(s) de ${data.total} encontrado(s)`;
      if (data.failed > 0 && data.failed === data.total) {
        toast.error(`Nenhum modelo foi sincronizado (${data.failed} com erro). Tente de novo em alguns minutos.`);
      } else if (data.failed > 0) {
        toast.warning(`Sincronizado em parte: ${resumo}. ${data.failed} não puderam ser atualizados.`);
      } else {
        toast.success(`Sincronizado! ${resumo}.`);
      }
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const handleEdit = (t: any) => navigate(`/templates/${t.id}/edit`);
  const handleNew = () => navigate("/templates/new");

  const handlePreviewOnly = (t: any) => {
    setPreviewId(t.id);
    setPreviewName(t.name);
    setPreviewOnly({
      name: t.name, category: t.category, language: t.language,
      headerType: t.headerType ?? "none", headerContent: t.headerContent ?? "",
      bodyText: t.bodyText, footerText: t.footerText ?? "",
      buttons: (t.buttons as Button_[]) ?? [], variableCount: t.variableCount ?? 0,
      notes: t.notes ?? "",
    });
  };

  // Filtros
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];
    const q = search.trim().toLowerCase();
    return templates.filter((t: any) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      if (q && !t.name.toLowerCase().includes(q) && !(t.bodyText || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [templates, search, statusFilter, categoryFilter]);

  return (
    <PermissionGuard permission="whatsapp:manage_templates">
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-semibold">Templates</h1>
            <p className="text-muted-foreground text-xs mt-0.5">
              Mensagens aprovadas pela Meta para envio em massa.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncMut.isPending ? "animate-spin" : ""}`} />
              {syncMut.isPending ? "Sincronizando..." : "Importar da Meta"}
            </Button>
            <Button size="sm" onClick={handleNew}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo
            </Button>
          </div>
        </div>

        {/* Stats */}
        <StatsBar templates={templates} />

        {/* Toolbar: search + filters */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou texto do corpo..."
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="approved">Aprovados</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="draft">Rascunhos</SelectItem>
              <SelectItem value="rejected">Rejeitados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas categorias</SelectItem>
              <SelectItem value="MARKETING">Marketing</SelectItem>
              <SelectItem value="UTILITY">Utilidade</SelectItem>
              <SelectItem value="AUTHENTICATION">Autenticação</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Lista */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-40 bg-muted animate-pulse rounded-xl" />)}
          </div>
        ) : filteredTemplates.length === 0 && (templates?.length ?? 0) > 0 ? (
          <div className="text-center py-16 rounded-xl border border-dashed">
            <Search className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground font-medium">Nenhum template corresponde aos filtros</p>
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => { setSearch(""); setStatusFilter("all"); setCategoryFilter("all"); }}>
              Limpar filtros
            </Button>
          </div>
        ) : templates?.length === 0 ? (
          <div className="text-center py-20 rounded-xl border border-dashed bg-gradient-to-br from-emerald-50/30 to-transparent">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
              <FileText className="h-8 w-8 text-emerald-600" strokeWidth={1.5} />
            </div>
            <p className="font-semibold text-lg">Nenhum template criado ainda</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              Templates são mensagens aprovadas pela Meta que você pode disparar para muitos contatos. Suportam texto, imagens, vídeos, documentos e botões interativos.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button onClick={() => syncMut.mutate()} variant="outline" size="sm" disabled={syncMut.isPending}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${syncMut.isPending ? "animate-spin" : ""}`} />
                Importar da Meta
              </Button>
              <Button onClick={handleNew} size="sm">
                <Plus className="h-4 w-4 mr-1.5" /> Criar primeiro template
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredTemplates.map((t: any) => (
              <TemplateCard
                key={t.id}
                t={t}
                onEdit={handleEdit}
                onDelete={(id) => deleteMut.mutate({ id })}
                onPreview={handlePreviewOnly}
                onSubmit={(t) => submitMut.mutate({ templateId: t.id })}
                onCheckStatus={(t) => checkStatusMut.mutate({ templateId: t.id })}
                submitPending={submitMut.isPending && submitMut.variables?.templateId === t.id}
                checkPending={checkStatusMut.isPending && checkStatusMut.variables?.templateId === t.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview-only Dialog (modal pequeno só pra visualizar) */}
      <Dialog open={previewOnly !== null} onOpenChange={(o) => { if (!o) { setPreviewOnly(null); setPreviewId(null); setPreviewName(""); } }}>
        <DialogContent className="w-[95vw] max-w-md mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>Preview: {previewName}</DialogTitle>
          </DialogHeader>
          {previewOnly && <WhatsAppPreview form={previewOnly} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPreviewOnly(null); setPreviewId(null); setPreviewName(""); }}>Fechar</Button>
            <Button onClick={() => { if (previewId) navigate(`/templates/${previewId}/edit`); }}>
              <Edit2 className="h-4 w-4 mr-1.5" /> Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}

